// Lote UX Leyro parity — listing directo do marketplace estilo Leyro.
//
// Cambios vs iter-anterior:
//   • Eliminado o hero con imaxe de vaca + buscador centrado. O hero queda
//     na landing (/) e a listing vai directo a grid coa CategoryBar sticky.
//   • Os selects de "Tipo" / "Provincia" eliminados — agora vénen da
//     CategoryBar (chips + popover Filtros) en Layout.tsx.
//   • Estado de filtros sigue na URL (?destination=...&province=...); a
//     CategoryBar le/escribe os mesmos params, así que cero refactor en
//     lóxica de fetch. Pura migración visual.

import { Beef, Gavel, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { searchMarketplace } from '../api/marketplace';
import { getPlatformStatus } from '../api/platform';
import { useAuth } from '../auth/useAuth';
import type { MarketplaceSaleSummary } from '@leyrochain/shared';
import PrivateBetaLanding from './PrivateBetaLanding';

type DestFilter = 'ALL' | 'BEEF' | 'DAIRY' | 'BREEDING' | 'AUCTION';
type ProvinceFilter = 'ALL' | 'A_CORUNA' | 'LUGO' | 'OURENSE' | 'PONTEVEDRA';

export default function MarketplacePage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const destination = (params.get('destination') as DestFilter | null) ?? 'ALL';
  const province = (params.get('province') as ProvinceFilter | null) ?? 'ALL';

  // Modo beta privado: usuarios anónimos non ven catálogo. Os autenticados
  // si — son contas piloto. O check faise ao cargar a páxina; mentres se
  // resolve mostramos os skeletons xa existentes.
  const [betaMode, setBetaMode] = useState<boolean | null>(null);
  useEffect(() => {
    if (user) {
      setBetaMode(false);
      return;
    }
    let alive = true;
    getPlatformStatus()
      .then((s) => {
        if (alive) setBetaMode(s.private_beta_mode);
      })
      .catch(() => {
        if (alive) setBetaMode(false);
      });
    return () => {
      alive = false;
    };
  }, [user]);

  const [results, setResults] = useState<MarketplaceSaleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const dest = destination === 'AUCTION' || destination === 'ALL' ? undefined : destination;
        const remote = await searchMarketplace({
          destination: dest,
          verifiedOnly: false,
        });
        if (!alive) return;
        let list = remote.results;
        if (destination === 'AUCTION') {
          list = list.filter((r) => r.sale_mode === 'AUCTION');
        }
        setResults(list);
        setError(null);
      } catch {
        if (alive) setError('Non se puido cargar o catálogo. Proba a recargar a páxina.');
      } finally {
        if (alive) setLoading(false);
      }
    }, 200);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [destination]);

  // Filtrado client-side por provincia (TODO: mover ao backend).
  const filteredResults = useMemo(() => {
    if (province === 'ALL') return results;
    return results.filter((r) => provinceFromMunicipality(r.seller.farm_municipality) === province);
  }, [results, province]);
  const total = filteredResults.length;

  // Curtocircuíto: anónimo + beta mode → landing especial.
  if (!user && betaMode) {
    return <PrivateBetaLanding />;
  }

  return (
    <section className="max-w-[1760px] mx-auto px-6 py-10">
      {loading && filteredResults.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="rounded-2xl bg-subtle animate-pulse h-72" />
          ))}
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-50 border border-danger text-danger text-sm">
          {error}
        </div>
      ) : filteredResults.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl" aria-hidden>
            🐄
          </div>
          <p className="mt-3 text-lg font-bold text-dark">{t('marketplace.noResults')}</p>
          <p className="text-muted text-sm mt-1">{t('marketplace.noResultsHint')}</p>
        </div>
      ) : (
        <>
          <div className="mb-5 flex items-end justify-between">
            <h2 className="text-base font-semibold text-dark">
              {t('marketplace.salesCount', { count: total })}
            </h2>
            <Link
              to="/marketplace/vender"
              className="text-sm font-semibold text-rauxa hover:underline"
            >
              {t('marketplace.sellCta')}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredResults.map((r) => (
              <SaleCard key={r.id} sale={r} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function SaleCard({ sale }: { sale: MarketplaceSaleSummary }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Link
      to={`/marketplace/animal/${sale.id}`}
      className="group block rounded-2xl overflow-hidden hover:shadow-card-hover transition-shadow"
    >
      <div className="relative aspect-[4/3] bg-subtle overflow-hidden">
        {sale.animal.photo_url ? (
          <img
            src={sale.animal.photo_url}
            alt={sale.animal.name ?? sale.animal.crotal_id}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-7xl text-rauxa/30">
            <Beef size={80} />
          </div>
        )}
        {sale.sale_mode === 'AUCTION' && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold uppercase tracking-wide">
            <Gavel size={12} className="inline mr-1" />
            {t('marketplace.badges.auction')}
          </span>
        )}
        {sale.seller.verified && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-white/90 backdrop-blur text-rauxa text-[10px] font-bold uppercase tracking-wide">
            <ShieldCheck size={12} className="inline mr-1" />
            {t('marketplace.badges.verified')}
          </span>
        )}
      </div>
      <div className="pt-3 pb-1 flex flex-col gap-1">
        <div className="flex justify-between items-baseline gap-2">
          <h3 className="font-semibold text-dark text-base truncate">
            {sale.animal.name ?? sale.animal.crotal_id}
          </h3>
          <span className="font-extrabold text-rauxa text-base whitespace-nowrap">
            {sale.sale_mode === 'AUCTION' && sale.top_bid_cents !== null
              ? formatPrice(sale.top_bid_cents)
              : formatPrice(sale.price_cents)}
          </span>
        </div>
        <p className="text-sm text-muted truncate">
          {sale.seller.farm_name ?? sale.seller.name}
          {sale.seller.farm_municipality ? ` · ${sale.seller.farm_municipality}` : ''}
        </p>
      </div>
    </Link>
  );
}

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(0)} €`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stub provincia: mentres o backend non expón `farm_province`, deducimos
// a provincia a partir do nome do concello cunha lista mínima. É un
// fallback educado — devolve null se non recoñece o concello (e nese
// caso o filtro non lle ve coincidencia).
// ─────────────────────────────────────────────────────────────────────────────

const CONCELLOS_POR_PROVINCIA: Record<ProvinceFilter, string[]> = {
  ALL: [],
  A_CORUNA: ['A Coruña', 'Santiago', 'Ferrol', 'Carballo', 'Noia', 'Padrón', 'Betanzos', 'Arzúa'],
  LUGO: ['Lugo', 'Monforte', 'Sarria', 'Vilalba', 'Foz', 'Ribadeo', 'Chantada'],
  OURENSE: ['Ourense', 'O Carballiño', 'Verín', 'Xinzo', 'Ribadavia', 'A Rúa', 'O Barco'],
  PONTEVEDRA: [
    'Pontevedra',
    'Vigo',
    'Lalín',
    'A Estrada',
    'Vilagarcía',
    'Cangas',
    'Marín',
    'Tui',
    'Cambados',
    'Sanxenxo',
  ],
};

function provinceFromMunicipality(municipality?: string | null): ProvinceFilter | null {
  if (!municipality) return null;
  const m = municipality.trim().toLowerCase();
  for (const [prov, list] of Object.entries(CONCELLOS_POR_PROVINCIA) as [
    ProvinceFilter,
    string[],
  ][]) {
    if (prov === 'ALL') continue;
    if (list.some((c) => m.includes(c.toLowerCase()))) return prov;
  }
  return null;
}
